import { Request, Response } from "express";
import mongoose from "mongoose";

import { SupportTicket } from "../models/support-ticket";
import { User } from "../models/user";
import { notificationService } from "../services/notification-service";
import { logger } from "../utils/logger";

interface AuthRequest extends Request {
  user?: {
    userId: string;
    email: string;
    role: string;
    schoolId?: string;
  };
}

interface APIResponse {
  success: boolean;
  message: string;
  data?: unknown;
  error?: string;
}

class SupportController {
  /**
   * Create a new support ticket
   * @route POST /api/v1/support/tickets
   * @access Private (All authenticated users)
   */
  async createTicket(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { subject, category, priority, description, tags } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const files = req.files as Express.Multer.File[];

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
          error: "Unauthorized",
        } as APIResponse);
        return;
      }

      // Validate required fields
      if (!subject || !description) {
        res.status(400).json({
          success: false,
          message: "Subject and description are required",
          error: "Missing required fields",
        } as APIResponse);
        return;
      }

      // Process attachments
      const attachments =
        files?.map(file => ({
          fileName: file.filename,
          originalName: file.originalname,
          fileSize: file.size,
          mimeType: file.mimetype,
          fileUrl: `/uploads/documents/${file.filename}`,
          uploadedAt: new Date(),
        })) || [];

      // Create ticket
      const ticket = new SupportTicket({
        userId: mongoose.Types.ObjectId.createFromHexString(userId),
        schoolId: schoolId
          ? mongoose.Types.ObjectId.createFromHexString(schoolId)
          : undefined,
        subject,
        category: category || "general",
        priority: priority || "medium",
        description,
        attachments,
        tags: tags ? (Array.isArray(tags) ? tags : [tags]) : [],
      });

      await ticket.save();

      // Notify user about ticket creation
      await notificationService.notifySupportTicketStatus(
        ticket.userId,
        ticket._id.toString(),
        "created",
        ticket.subject,
      );

      logger.info(`Support ticket created: ${ticket.ticketNumber}`, {
        userId,
        ticketId: ticket._id,
      });

      res.status(201).json({
        success: true,
        message: "Support ticket created successfully",
        data: {
          ticket: {
            id: ticket._id,
            ticketNumber: ticket.ticketNumber,
            subject: ticket.subject,
            category: ticket.category,
            priority: ticket.priority,
            status: ticket.status,
            description: ticket.description,
            attachments: ticket.attachments,
            createdAt: ticket.createdAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error creating support ticket:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create support ticket",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get user's support tickets
   * @route GET /api/v1/support/tickets
   * @access Private (All authenticated users)
   */
  async getUserTickets(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { status, category, page = "1", limit = "10" } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
          error: "Unauthorized",
        } as APIResponse);
        return;
      }

      const query: Record<string, unknown> = {
        userId: mongoose.Types.ObjectId.createFromHexString(userId),
      };

      if (status) {
        query.status = status;
      }

      if (category) {
        query.category = category;
      }

      const pageNumber = Number.parseInt(page as string, 10);
      const limitNumber = Number.parseInt(limit as string, 10);
      const skip = (pageNumber - 1) * limitNumber;

      const [tickets, total] = await Promise.all([
        SupportTicket.find({ ...query })
          .sort({ createdAt: -1 })
          .skip(skip)
          .limit(limitNumber)
          .select("-messages"),
        SupportTicket.countDocuments({ ...query }),
      ]);

      res.status(200).json({
        success: true,
        message: "Support tickets retrieved successfully",
        data: {
          tickets,
          pagination: {
            total,
            page: pageNumber,
            limit: limitNumber,
            pages: Math.ceil(total / limitNumber),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching support tickets:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch support tickets",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get ticket details with messages
   * @route GET /api/v1/support/tickets/:id
   * @access Private (All authenticated users)
   */
  async getTicketDetails(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
          error: "Unauthorized",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid ticket ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const ticket = await SupportTicket.findOne({
        _id: id,
        userId: mongoose.Types.ObjectId.createFromHexString(userId),
      }).populate("userId", "email fullName");

      if (!ticket) {
        res.status(404).json({
          success: false,
          message: "Ticket not found",
          error: "Not found",
        } as APIResponse);
        return;
      }

      // Mark messages as read
      let hasUnreadMessages = false;
      for (const message of ticket.messages) {
        if (!message.isRead && message.senderRole !== "user") {
          message.isRead = true;
          hasUnreadMessages = true;
        }
      }

      if (hasUnreadMessages) {
        await ticket.save();
      }

      res.status(200).json({
        success: true,
        message: "Ticket details retrieved successfully",
        data: { ticket },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching ticket details:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch ticket details",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Add message to ticket
   * @route POST /api/v1/support/tickets/:id/messages
   * @access Private (All authenticated users)
   */
  async addMessage(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { message } = req.body;
      const userId = req.user?.userId;
      const files = req.files as Express.Multer.File[];

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
          error: "Unauthorized",
        } as APIResponse);
        return;
      }

      if (!message) {
        res.status(400).json({
          success: false,
          message: "Message is required",
          error: "Missing required field",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid ticket ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const ticket = await SupportTicket.findOne({
        _id: id,
        userId: mongoose.Types.ObjectId.createFromHexString(userId),
      });

      if (!ticket) {
        res.status(404).json({
          success: false,
          message: "Ticket not found",
          error: "Not found",
        } as APIResponse);
        return;
      }

      // Process attachments
      const attachments =
        files?.map(file => ({
          fileName: file.filename,
          originalName: file.originalname,
          fileSize: file.size,
          mimeType: file.mimetype,
          fileUrl: `/uploads/documents/${file.filename}`,
          uploadedAt: new Date(),
        })) || [];

      // Add message
      ticket.messages.push({
        senderId: mongoose.Types.ObjectId.createFromHexString(userId),
        senderRole: "user",
        message,
        attachments,
        sentAt: new Date(),
        isRead: false,
      } as any);

      // Update ticket status if it was resolved
      if (ticket.status === "resolved" || ticket.status === "closed") {
        ticket.status = "open";
      }

      await ticket.save();

      // Notify support team (in a real system, you'd notify support staff)
      // For now, we'll just log it
      logger.info(`New message added to ticket ${ticket.ticketNumber}`, {
        userId,
        ticketId: ticket._id,
      });

      res.status(201).json({
        success: true,
        message: "Message added successfully",
        data: {
          message: ticket.messages.at(-1),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error adding message to ticket:", error);
      res.status(500).json({
        success: false,
        message: "Failed to add message",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Close ticket
   * @route PUT /api/v1/support/tickets/:id/close
   * @access Private (All authenticated users)
   */
  async closeTicket(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
          error: "Unauthorized",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid ticket ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const ticket = await SupportTicket.findOne({
        _id: id,
        userId: mongoose.Types.ObjectId.createFromHexString(userId),
      });

      if (!ticket) {
        res.status(404).json({
          success: false,
          message: "Ticket not found",
          error: "Not found",
        } as APIResponse);
        return;
      }

      ticket.status = "closed";
      ticket.closedAt = new Date();
      await ticket.save();

      logger.info(`Ticket closed: ${ticket.ticketNumber}`, {
        userId,
        ticketId: ticket._id,
      });

      res.status(200).json({
        success: true,
        message: "Ticket closed successfully",
        data: { ticket },
      } as APIResponse);
    } catch (error) {
      logger.error("Error closing ticket:", error);
      res.status(500).json({
        success: false,
        message: "Failed to close ticket",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Admin: Reply to ticket (for support staff)
   * @route POST /api/v1/admin/support/tickets/:id/reply
   * @access Private (Admin/Support)
   */
  async replyToTicket(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { message, status } = req.body;
      const userId = req.user?.userId;
      const userRole = req.user?.role;
      const files = req.files as Express.Multer.File[];

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
          error: "Unauthorized",
        } as APIResponse);
        return;
      }

      // Only admin and super_admin can reply
      if (userRole !== "admin" && userRole !== "super_admin") {
        res.status(403).json({
          success: false,
          message: "Only support staff can reply to tickets",
          error: "Forbidden",
        } as APIResponse);
        return;
      }

      if (!message) {
        res.status(400).json({
          success: false,
          message: "Message is required",
          error: "Missing required field",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid ticket ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const ticket = await SupportTicket.findById(id);

      if (!ticket) {
        res.status(404).json({
          success: false,
          message: "Ticket not found",
          error: "Not found",
        } as APIResponse);
        return;
      }

      // Process attachments
      const attachments =
        files?.map(file => ({
          fileName: file.filename,
          originalName: file.originalname,
          fileSize: file.size,
          mimeType: file.mimetype,
          fileUrl: `/uploads/documents/${file.filename}`,
          uploadedAt: new Date(),
        })) || [];

      // Add support reply
      ticket.messages.push({
        senderId: mongoose.Types.ObjectId.createFromHexString(userId),
        senderRole: "support",
        message,
        attachments,
        sentAt: new Date(),
        isRead: false,
      } as any);

      // Update status if provided
      if (
        status &&
        ["in_progress", "waiting_response", "resolved"].includes(status)
      ) {
        ticket.status = status;
        if (status === "resolved") {
          ticket.resolvedAt = new Date();
          ticket.resolvedBy =
            mongoose.Types.ObjectId.createFromHexString(userId);
        }
      }

      await ticket.save();

      // Notify user about support reply
      await notificationService.notifySupportMessage(
        ticket.userId,
        ticket._id.toString(),
        message,
        true,
      );

      if (status === "resolved") {
        await notificationService.notifySupportTicketStatus(
          ticket.userId,
          ticket._id.toString(),
          "resolved",
          ticket.subject,
        );
      }

      logger.info(`Support replied to ticket ${ticket.ticketNumber}`, {
        supportUserId: userId,
        ticketId: ticket._id,
      });

      res.status(201).json({
        success: true,
        message: "Reply sent successfully",
        data: {
          message: ticket.messages.at(-1),
          ticket: {
            id: ticket._id,
            status: ticket.status,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error replying to ticket:", error);
      res.status(500).json({
        success: false,
        message: "Failed to send reply",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Admin: Get all support tickets
   * @route GET /api/v1/admin/support/tickets
   * @access Private (Admin/Support)
   */
  async getAllTickets(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userRole = req.user?.role;

      if (userRole !== "admin" && userRole !== "super_admin") {
        res.status(403).json({
          success: false,
          message: "Only support staff can view all tickets",
          error: "Forbidden",
        } as APIResponse);
        return;
      }

      const {
        status,
        category,
        priority,
        page = "1",
        limit = "20",
      } = req.query;

      const query: Record<string, unknown> = {};

      if (status) {
        query.status = status;
      }

      if (category) {
        query.category = category;
      }

      if (priority) {
        query.priority = priority;
      }

      const pageNumber = Number.parseInt(page as string, 10);
      const limitNumber = Number.parseInt(limit as string, 10);
      const skip = (pageNumber - 1) * limitNumber;

      const [tickets, total] = await Promise.all([
        SupportTicket.find({ ...query })
          .sort({ createdAt: -1 })
          .skip(skip)
          .limit(limitNumber)
          .populate("userId", "email fullName role")
          .select("-messages"),
        SupportTicket.countDocuments({ ...query }),
      ]);

      res.status(200).json({
        success: true,
        message: "Support tickets retrieved successfully",
        data: {
          tickets,
          pagination: {
            total,
            page: pageNumber,
            limit: limitNumber,
            pages: Math.ceil(total / limitNumber),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching all support tickets:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch support tickets",
        error: "Internal server error",
      } as APIResponse);
    }
  }
}

export const supportController = new SupportController();
